import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getMiCuentaEmprendedor,
  loginEmprendedor,
  type ComercioStore,
  type ProductoStore,
  updateMiCuentaEmprendedor,
} from '../services/marketplaceService.tsx';

export default function EmprendedorCuenta() {
  const navigate = useNavigate();
  const [token, setToken] = useState<string>(localStorage.getItem('emprendedorToken') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [login, setLogin] = useState({ email: '', password: '' });
  const [comercio, setComercio] = useState<ComercioStore | null>(null);
  const [productos, setProductos] = useState<ProductoStore[]>([]);

  const [perfil, setPerfil] = useState({ nombre_marca: '', rubro: '', telefono: '', whatsapp: '', bio: '' });

  const load = async (tk: string) => {
    const data = await getMiCuentaEmprendedor(tk);
    setComercio(data.comercio);
    setProductos(data.productos || []);
    const e: any = data.comercio?.emprendedor || {};
    setPerfil({
      nombre_marca: e.nombreMarca || data.comercio.nombre || '',
      rubro: e.rubro || '',
      telefono: e.telefono || '',
      whatsapp: e.whatsapp || '',
      bio: e.bio || '',
    });
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        await load(token);
      } catch {
        setError('Sesión vencida. Iniciá sesión nuevamente.');
        localStorage.removeItem('emprendedorToken');
        setToken('');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const doLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await loginEmprendedor(login);
      localStorage.setItem('emprendedorToken', resp.token);
      setToken(resp.token);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const guardarPerfil = async () => {
    if (!token) return;
    try {
      setLoading(true);
      await updateMiCuentaEmprendedor(token, perfil);
      await load(token);
    } finally {
      setLoading(false);
    }
  };

  const solicitarAcompanamiento = async () => {
    if (!token) return;
    try {
      setLoading(true);
      await updateMiCuentaEmprendedor(token, {
        estado: 'en_formalizacion',
        acompanamiento: { solicitado: true, arca: true, nota: 'Solicita acompañamiento para alta ARCA/monotributo' },
      });
      await load(token);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !comercio) {
    return (
      <div className="min-h-screen bg-[#eef6ff] p-4 sm:p-6">
        <div className="mx-auto max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-sm">
          <button onClick={() => navigate('/emprendedores')} className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700">
            <ArrowLeft className="h-4 w-4" /> Volver
          </button>
          <h1 className="text-xl font-bold">Mi cuenta emprendedora</h1>
          {error && <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <input type="email" className="w-full rounded border p-2" placeholder="Email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
          <input type="password" className="w-full rounded border p-2" placeholder="Contraseña" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
          <button onClick={doLogin} disabled={loading} className="w-full rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </div>
      </div>
    );
  }

  const estado = (comercio as any)?.emprendedor?.estado || 'informal';

  return (
    <div className="min-h-screen bg-[#eef6ff] p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <h1 className="text-2xl font-bold">Mi emprendimiento</h1>
          <p className="text-sm text-slate-600">Gestioná tu perfil y seguí el acompañamiento para ARCA/Monotributo.</p>
          <p className="mt-2 text-sm">Estado: <b>{estado}</b></p>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
          <h2 className="text-lg font-semibold">Perfil público</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="rounded border p-2" placeholder="Marca" value={perfil.nombre_marca} onChange={(e) => setPerfil({ ...perfil, nombre_marca: e.target.value })} />
            <input className="rounded border p-2" placeholder="Rubro" value={perfil.rubro} onChange={(e) => setPerfil({ ...perfil, rubro: e.target.value })} />
            <input className="rounded border p-2" placeholder="Teléfono" value={perfil.telefono} onChange={(e) => setPerfil({ ...perfil, telefono: e.target.value })} />
            <input className="rounded border p-2" placeholder="WhatsApp" value={perfil.whatsapp} onChange={(e) => setPerfil({ ...perfil, whatsapp: e.target.value })} />
            <textarea className="sm:col-span-2 rounded border p-2" rows={3} placeholder="Bio" value={perfil.bio} onChange={(e) => setPerfil({ ...perfil, bio: e.target.value })} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={guardarPerfil} disabled={loading} className="rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Guardar cambios</button>
            <button onClick={solicitarAcompanamiento} disabled={loading} className="rounded border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60">
              Quiero acompañamiento para registrarme en ARCA
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Mis productos publicados</h2>
          <p className="text-sm text-slate-600">Total: {productos.length}</p>
          {estado === 'en_formalizacion' && (
            <p className="mt-3 inline-flex items-center gap-2 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Solicitud de acompañamiento enviada. La Cámara te contactará para alta en ARCA.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
